import React from 'react'
import { useTranslation } from 'react-i18next'
import { EllipsisVertical } from 'lucide-react'
import type { CanvasProjectTitleProps } from './CanvasProjectTitle.types'
import './CanvasProjectTitle.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { ProjectIcon } from '@/src/renderer/components/common/ProjectIcon'
import { CanvasTextContent } from './nodes/CanvasTextContent'
import { PROJECT_TITLE_PALETTE } from '../ProjectTitle/titleTextPalette'
import '../ProjectTitle/ProjectTitle.scss'

/**
 * Der Name des Projekts oben links im **maximierten** Projekt-Canvas, samt
 * einem Knopf für die **Projektoptionen** (Figma `Frame 624846`, 4931:265855).
 *
 * **Warum nur maximiert:** Dort ist die **Aufgabenliste** nach links aus dem
 * Bild gefahren und mit ihr die Breadcrumb — der Canvas füllt den Schirm, und
 * nichts sagt mehr, in welchem Projekt man arbeitet. Geteilt steht die
 * Breadcrumb daneben; der Titel stünde doppelt da. Die Bedingung trägt das
 * Panel (siehe dort), nicht dieses Bauteil.
 *
 * Es ist das Gegenstück zur Aktionsgruppe oben rechts und teilt sich mit ihr
 * die Maße: dieselbe Lage zur Kante, dasselbe Polster, dieselbe Rundung. Die
 * Fläche darunter, die das **Punktraster** ruhen lässt, liegt im
 * `ProjectCanvasPanel` — sie gehört zur Lage auf dem Canvas, nicht zum Inhalt.
 *
 * @example
 * <CanvasProjectTitle title={project.name} />
 */
export const CanvasProjectTitle: React.FC<CanvasProjectTitleProps> = ({
  title,
  titleHtml,
  icon,
  iconColor,
  onOpenOptions,
  className,
}) => {
  const { t } = useTranslation()

  // Ohne Namen gar nichts: Eine leere Platte, die auf den Titel wartet, wäre
  // ein Kasten ohne Aussage — und sie deckte dabei das Punktraster ab.
  if (!title) return null

  return (
    <div className={`canvas-project-title${className ? ` ${className}` : ''}`}>
      {/*
        Der volle Name im `title`: Die Zeile wird bei langen Projektnamen
        abgeschnitten (siehe SCSS), und wer ihn ganz lesen will, soll ihn
        erreichen können.
      */}
      {icon && <ProjectIcon icon={icon} iconColor={iconColor} size={20} className="canvas-project-title__icon" />}
      <WithTooltip content={title}>
        <span className="canvas-project-title__name project-title-ink">
          {titleHtml ? <CanvasTextContent content={titleHtml} palette={PROJECT_TITLE_PALETTE} /> : title}
        </span>
      </WithTooltip>

      {/*
        **Projektoptionen** — noch ohne Funktion.

        Der Knopf steht bewusst schon da, klickbar und wirkungslos, wie
        `CanvasShareButton` daneben: Die Optionen kommen als Nächstes, und ein
        Knopf, der später an derselben Stelle erscheint, verschiebt das Layout
        ein zweites Mal. Wer sie baut, hängt hier `onOpenOptions` ein.
      */}
      <WithTooltip content={t('projectCanvas.projectOptions')}>
        <button
          type="button"
          className="canvas-project-title__options"
          aria-label={t('projectCanvas.projectOptions')}
          onClick={onOpenOptions}
        >
          <EllipsisVertical size={16} strokeWidth={2} />
        </button>
      </WithTooltip>
    </div>
  )
}
